import React, { useEffect, useMemo, useState } from "react";
import Menu from "./components/menu/Menu";
import styles from "./App.module.css";
import TripPublication from "./components/tripPublication/TripPublication";
import TripReservation from "./components/tripReservation/TripReservation";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import AddOrganisme from "./components/organisme/AddOrganisme";
import ManageEstablishment from "./components/manageEstablishment/ManageEstablishment";
import AddEstablishementNew from "./components/manageEstablishment/addEstablishementNew/AddEstablishementNew";
import ViewEstablishementNew from "./components/manageEstablishment/viewEstablishementNew/ViewEstablishementNew";
import { AdminProvider } from "./hooks/useAdmin";
import { useAdminAccessControl } from "./hooks/useAdminAccessControl";
import AddTrip from "./components/tripPublication/addTrip/AddTrip";
import PublishTrip from "./components/tripPublication/publishTripNew/PublishTrip";
import TripView from "./components/tripPublication/tripView/TripView";
import Organisme from "./components/organisme/Organisme";
import { Icon, Text } from "@zorni/components-lib";
import ManageUsers from "./components/manageUsers/ManageUsers/ManageUsers";
import { getActiveAdminRouteMeta } from "./navigation/adminRouteMeta";
import AdminHeader from "./components/adminHeader/AdminHeader";
import useReactSimpleMatchMedia from "react-simple-matchmedia";
import PartnerAnalyticsDashboard from "./components/partnerAnalyticsDashboard/PartnerAnalyticsDashboard";
import Inbox from "./components/inbox/Inbox";
import AccountActivationRequired from "./common/accountActivation/AccountActivationRequired";
import EstablishmentsLoadError from "./common/establishmentsLoadError/EstablishmentsLoadError";
import Preloader from "../common/preLoader/PreLoader";

function AdminOverview() {
  const cards = [
    { key: "users", icon: "iconUsers", label: "Utilisateurs actifs", value: "1 245" },
    { key: "res", icon: "iconCalendar", label: "Réservations", value: "487" },
    { key: "est", icon: "iconHotel", label: "Établissements", value: "312" },
    { key: "org", icon: "iconUserPlus", label: "Nouveaux comptes", value: "54" },
  ];

  return (
    <section className={styles.overview}>
      <div className={styles.overviewIntro}>
        <Text as="h2" size="xxl" weight="bold">
          Vue d&apos;ensemble du dashboard
        </Text>
        <Text as="p" size="md" color="muted">
          Gérez votre plateforme et analysez les indicateurs clés.
        </Text>
      </div>

      <div className={styles.overviewGrid}>
        {cards.map((card) => (
          <article key={card.key} className={styles.overviewCard}>
            <div>
              <Text as="p" size="xs" weight="bold" className={styles.overviewLabel}>
                {card.label}
              </Text>
              <Text as="p" size="xxl" weight="bold">
                {card.value}
              </Text>
            </div>

            <span className={styles.overviewIcon}>
              <Icon name={card.icon} size="lg" color="primary" />
            </span>
          </article>
        ))}
      </div>
    </section>
  );
}


export function AppDashboard() {
  return (
    <AdminProvider>
      <AppDashboardContent />
    </AdminProvider>
  );
}

function AppDashboardContent() {
  const location = useLocation();
  // Doit correspondre exactement au breakpoint CSS (App.module.css,
  // @media max-width:1024px) qui bascule la sidebar en mode off-canvas.
  // Un JS/CSS désaligné (avant : 767px ici vs 1024px en CSS) laissait la
  // sidebar ouverte en permanence et non fermable entre 768-1024px (iPad
  // mini/Air/Pro 11 portrait) — elle cachait alors une partie du contenu.
  const isMobile = useReactSimpleMatchMedia("(max-width: 1024px)");
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);

  const activeRouteMeta = useMemo(
    () => getActiveAdminRouteMeta(location.pathname || ""),
    [location.pathname]
  );

  const {
    isAdminAccessBlocked,
    canAccessOrganisme,
    canAccessEstablishments,
    isCheckingEstablishments,
    hasEstablishmentsLoadError,
    refetchEstablishments,
  } = useAdminAccessControl();

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      return;
    }
    setSidebarOpen(true);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  // Parcours d'activation strictement séquentiel (usePerson.status →
  // useAdmin.organization → établissements) : tant qu'une étape n'est pas
  // terminée, sa route (et toutes celles qui en dépendent) affichent le
  // composant d'activation au lieu de leur contenu réel — impossible à
  // contourner en changeant d'URL puisque c'est la route elle-même qui est
  // remplacée. Voir useAdminAccessControl pour le détail des règles.
  // Le déverrouillage dépend de la liste des établissements (AdminProvider) :
  // tant qu'elle se charge on affiche un loader plutôt que le gate, sinon
  // celui-ci clignoterait avant de laisser place au contenu ; si elle échoue,
  // un bloc « Réessayer » plutôt que le gate, qui affirmerait à tort des
  // étapes manquantes.
  const gate = isCheckingEstablishments ? (
    <div className={styles.adminContentLoading}>
      <Preloader />
    </div>
  ) : hasEstablishmentsLoadError ? (
    <EstablishmentsLoadError onRetry={refetchEstablishments} />
  ) : (
    <AccountActivationRequired />
  );
  const establishmentsElement = canAccessEstablishments ? undefined : gate;

  return (
    <div
      className={[
        styles.adminShell,
        !isMobile && isSidebarCollapsed ? styles.adminShellSidebarCollapsed : "",
      ].join(" ")}
    >
        <aside
          className={[
            styles.adminSidebar,
            isSidebarOpen ? styles.adminSidebarOpen : "",
          ].join(" ")}
        >
          <Menu
            onNavigate={() => isMobile && setSidebarOpen(false)}
            onToggleCollapse={() => setSidebarCollapsed((collapsed) => !collapsed)}
            isCollapsed={!isMobile && isSidebarCollapsed}
            isMobile={isMobile}
          />
        </aside>

        {isMobile && isSidebarOpen && (
          <button
            type="button"
            className={styles.sidebarBackdrop}
            aria-label="Fermer le menu"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div className={styles.adminViewport}>
          <div className={styles.adminHeader}>
            <AdminHeader
              activeRouteMeta={activeRouteMeta}
              isMobile={isMobile}
              onToggleSidebar={() => setSidebarOpen((open) => !open)}
            />
          </div>

          <main className={styles.adminContent}>
            <Routes>
              <Route
                index
                element={isAdminAccessBlocked ? gate : <PartnerAnalyticsDashboard />}
              />
              {/* Ancienne URL du dashboard (favoris) : il vit désormais sur /admin. */}
              <Route path="dashboard" element={<Navigate to="/admin" replace />} />
              <Route
                path="overview"
                element={isAdminAccessBlocked ? gate : <AdminOverview />}
              />
              <Route
                path="utilisateurs"
                element={isAdminAccessBlocked ? gate : <ManageUsers />}
              />
              <Route
                path="reservations"
                element={isAdminAccessBlocked ? gate : <TripReservation />}
              />
              <Route path="inbox" element={isAdminAccessBlocked ? gate : <Inbox />} />
              <Route
                path="circuits"
                element={isAdminAccessBlocked ? gate : <TripPublication />}
              />
              <Route
                path="circuits/add"
                element={isAdminAccessBlocked ? gate : <AddTrip />}
              />
              <Route
                path="circuits/trip/:tripId"
                element={isAdminAccessBlocked ? gate : <AddTrip />}
              />
              <Route
                path="circuits/:publicationId"
                element={isAdminAccessBlocked ? gate : <TripView />}
              />
              <Route
                path="circuits/:publicationId/:tripId/edit"
                element={isAdminAccessBlocked ? gate : <AddTrip />}
              />
              <Route
                path="circuits/:publicationId/publish"
                element={isAdminAccessBlocked ? gate : <PublishTrip />}
              />
              {/* organisme (étape 2) ne dépend que de l'étape 1 (profil) et
                  non de lui-même : sinon il serait impossible de jamais
                  créer l'organisme manquant. */}
              <Route
                path="organisme"
                element={canAccessOrganisme ? <Organisme /> : gate}
              />
              <Route
                path="organisme/:organismeId"
                element={canAccessOrganisme ? <AddOrganisme /> : gate}
              />
              <Route
                path="organisme/add"
                element={canAccessOrganisme ? <AddOrganisme /> : gate}
              />
              <Route
                path="establishments"
                element={establishmentsElement || <ManageEstablishment />}
              />
              <Route
                path="establishments/:establishmentId"
                element={establishmentsElement || <ViewEstablishementNew />}
              />
              <Route
                path="establishments/:establishmentId/edit"
                element={establishmentsElement || <AddEstablishementNew />}
              />
              <Route
                path="establishments/add"
                element={establishmentsElement || <AddEstablishementNew />}
              />
            </Routes>
          </main>
        </div>
    </div>
  );
}

export default AppDashboard;
